class MegaMenuProducts {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.hoverDelay = 150;
    this.hideDelay = 300;
    this.hoverTimeout = null;
    this.hideTimeout = null;
    this.currentPanel = null;
    this.currentMenuItem = null;
    this.isOverPanel = false;
    this.isOverMenu = false;

    this.init();
    // this.preloadCollections(); -- commented out for broken image links fix 
  }

  init() {
    const menuItems = document.querySelectorAll(
      '.wt-page-nav-mega__sublist__item--child[data-collection-handle], .wt-page-nav-mega__sublist__item--grandchild[data-collection-handle]'
    );

    menuItems.forEach(item => {
      item.addEventListener('mouseenter', () => this.handleMouseEnter(item));
      item.addEventListener('mouseleave', () => this.handleMouseLeave(item));
    });

    document.querySelectorAll('.wt-page-nav-mega__product-preview').forEach(panel => {
      panel.addEventListener('mouseenter', () => {
        this.isOverPanel = true;
        clearTimeout(this.hideTimeout);
      });

      panel.addEventListener('mouseleave', () => {
        this.isOverPanel = false;
        this.scheduleHide(panel);
      });
    });

    document.querySelectorAll('.wt-page-nav-mega__sublist__wrapper').forEach(wrapper => {
      wrapper.addEventListener('mouseenter', () => {
        this.isOverMenu = true;
      });

      wrapper.addEventListener('mouseleave', () => {
        this.isOverMenu = false;
        const panel = wrapper.querySelector('.wt-page-nav-mega__product-preview');
        if (panel) this.scheduleHide(panel);
      });
    });
  }

  preloadCollections() {
    if (!('requestIdleCallback' in window)) return;

    const items = [...document.querySelectorAll('[data-collection-handle]')];
    const collections = items.map(el => ({
      handle: el.dataset.collectionHandle,
      url: el.dataset.collectionUrl
    }));

    // Deduplicate by handle
    const uniqueCollections = [...new Map(collections.map(c => [c.handle, c])).values()];

    requestIdleCallback(() => {
      uniqueCollections.forEach(({ handle, url }) => {
        if (!this.cache.has(handle)) {
          this.fetchProducts(handle, url).catch(() => {});
        }
      });
    });
  }

  handleMouseEnter(item) {
    clearTimeout(this.hideTimeout);
    this.isOverMenu = true;

    const handle = item.dataset.collectionHandle;
    const url = item.dataset.collectionUrl;
    const serverCount = item.dataset.collectionCount ?? -1; //added sept 4th 2026
    const panel = item.closest('.wt-page-nav-mega__sublist__wrapper')
      .querySelector('.wt-page-nav-mega__product-preview');

    if (!panel) return;

    if (this.currentMenuItem === item && panel.classList.contains('active')) {
      return;
    }

    clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.currentMenuItem = item;
      this.currentPanel = panel;
      this.showPreview(panel, handle, url, serverCount); //added serverCount arg sept 4th 2026
    }, this.hoverDelay);
  }

  handleMouseLeave() {
    this.isOverMenu = true;
  }

  scheduleHide(panel) {
    clearTimeout(this.hideTimeout);

    this.hideTimeout = setTimeout(() => {
      if (!this.isOverPanel && !this.isOverMenu) {
        this.hidePreview(panel);
        this.currentMenuItem = null;
        this.currentPanel = null;
      }
    }, this.hideDelay);
  }

  async showPreview(panel, handle, url, serverCount = -1) { //edited sept 4th 2026
    if (panel.dataset.currentCollection === handle && panel.classList.contains('active')) {
      return;
    }

    panel.dataset.currentCollection = handle;
    panel.classList.add('active');

    const loader = panel.querySelector('.wt-page-nav-mega__product-preview__loader');
    const grid = panel.querySelector('.wt-page-nav-mega__product-preview__grid');

    if (!this.cache.has(handle)) {
      loader.style.display = 'flex';
      grid.style.opacity = '0';
    } else {
      grid.style.opacity = '1';
    }

    try {
      const data = await this.fetchProducts(handle, url, serverCount); //edited sept 4th 2026

      if (panel.dataset.currentCollection === handle) {
        this.renderProducts(panel, data.products, data.totalCount, url, handle);
        requestAnimationFrame(() => {
          grid.style.opacity = '1';
        });
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      // Show error message to user
      if (panel.dataset.currentCollection === handle) {
        grid.innerHTML = '<p style="padding: 20px; text-align: center; color: #666;">Unable to load products</p>';
        grid.style.opacity = '1';
      }
    } finally {
      loader.style.display = 'none';
    }
  }

  hidePreview(panel) {
    panel.classList.remove('active');
    panel.dataset.currentCollection = '';
    const grid = panel.querySelector('.wt-page-nav-mega__product-preview__grid');
    if (grid) grid.style.opacity = '0';
  }

  // Extract collection slug from URL
  extractCollectionSlug(url) {
    if (!url) return null;
    const match = url.match(/\/collections\/([^\/\?#]+)/);
    return match ? match[1] : null;
  }

  async fetchProducts(handle, url, serverCount = -1) { //edited sept 4th 2026
    if (this.cache.has(handle)) {
      return this.cache.get(handle);
    }

    if (this.pendingRequests.has(handle)) {
      return this.pendingRequests.get(handle);
    }

    const request = (async () => {
      const collectionSlug = this.extractCollectionSlug(url) || handle;

      const productsResponse = await fetch(
        `/collections/${collectionSlug}/products.json?limit=10`
      );

      if (!productsResponse.ok) {
        throw new Error(`Failed to fetch products: ${productsResponse.status} ${productsResponse.statusText}`);
      }

      const productsData = await productsResponse.json();

      const parsedServerCount = parseInt(serverCount, 10);
      const totalCount = (parsedServerCount >= 0)
        ? parsedServerCount
        : productsData.products.length;

      const result = {
        products: productsData.products,
        totalCount
      };

      this.cache.set(handle, result);
      this.pendingRequests.delete(handle);
      return result;
    })();

    this.pendingRequests.set(handle, request);
    return request;
  }

  renderProducts(panel, products, totalCount, url, handle) {
    const title = panel.querySelector('.wt-page-nav-mega__product-preview__title');
    const viewAll = panel.querySelector('.wt-page-nav-mega__product-preview__view-all');
    const grid = panel.querySelector('.wt-page-nav-mega__product-preview__grid');

    const name = handle
      .split('-')
      .map(w => w[0].toUpperCase() + w.slice(1))
      .join(' ');

    title.textContent = `${name} (${totalCount} ${totalCount === 1 ? 'Product' : 'Products'})`;
    viewAll.href = url;

    if (products.length === 0) {
      grid.innerHTML = '<p style="padding: 20px; text-align: center; color: #666;">No products found</p>';
      return;
    }

    grid.innerHTML = products.slice(0, 10).map(p => {
      let image = '';

      if (Array.isArray(p.images) && p.images.length && p.images[0]?.src) {
        image = p.images[0].src;
      } else if (typeof p.featured_image === 'string') {
        image = p.featured_image;
      } else if (p.featured_image?.src) {
        image = p.featured_image.src;
      }

       return `
        <a href="/products/${p.handle}" class="wt-page-nav-mega__product-card">
          <div class="wt-page-nav-mega__product-card__image">
            ${image
              ? `<img src="${image}${image.includes('?') ? '&' : '?'}width=300" loading="lazy" alt="${this.escapeHtml(p.title)}">`
              : '<div class="wt-page-nav-mega__product-card__no-image">No Image</div>'}
          </div>
          <h4 class="wt-page-nav-mega__product-card__title">${this.escapeHtml(p.title)}</h4>
        </a>
      `;
    }).join('');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', () => new MegaMenuProducts())
  : new MegaMenuProducts();